<template>
  <div v-if="visible" class="config-dialog">
    <div class="dialog-content">
      <div class="dialog-header-inner">
        <div class="small-title">
          <span>{{ dialogTitle }}</span>
        </div>
      </div>
      <el-tabs v-model="activeTab" class="dialog-tabs">
        <el-tab-pane label="行星" name="planets"></el-tab-pane>
        <el-tab-pane label="小行星" name="asteroids"></el-tab-pane>
        <el-tab-pane label="容许度设置" name="phases"></el-tab-pane>
      </el-tabs>
      <div class="dialog-body-content">
        <div v-if="activeTab !== 'phases'">
          <div class="selection-actions">
            <el-checkbox
              :value="isAllSelected"
              :indeterminate="isIndeterminate"
              @change="toggleSelectAll"
            >
              全选
            </el-checkbox>
            <!-- 搜索框（2026-10-13）：紧挨「全选」右侧 20px（不靠最右侧），
                 只过滤**当前标签页**（行星 / 小行星）下方的按钮列表，按按钮名称模糊匹配；
                 输入即过滤、清空即恢复全部；只影响展示，不影响选中态与请求参数。 -->
            <el-input
              v-model="searchKeyword"
              class="option-search"
              style="width:150px;margin-left:20px;"
              size="mini"
              clearable
              :placeholder="searchPlaceholder"
            />
          </div>
          <div class="options-container">
            <div
              class="option-item"
              v-for="planet in visiblePlanets"
              :key="planet.id"
            >
              <button
                type="button"
                class="option-button"
                :class="{ 'active': planet.selected }"
                @click="togglePlanetSelected(planet)"
              >
                <span class="option-label">{{ planet.name }}</span>
                <!-- 按钮高亮 = 「参与请求」态（planet.selected）：
                       - 点按钮选中=参与+展示；点按钮取消=不再展示；
                         取消只影响画布展示，**不会**把它移出后端请求参数（参与集合只增不减）。
                     右侧确认框只控制「画布展示」（取消勾选=透明隐藏，仍在请求里）。
                     例外：点「全选」会把全部行星并入请求；点「取消全选」可把请求收回。 -->
                <el-checkbox
                  class="option-canvas-check"
                  :value="planet.visibleOnCanvas === true"
                  @click.native.stop
                  @change="val => toggleCanvasVisible(planet, val)"
                />
              </button>
            </div>
          </div>
          <!-- 搜索无结果时的空态：必须放在本页签块**内部**。
               绝不能插在 v-if 与 v-else 之间 —— 那会抢走下一步 else 的配对，
               导致「容许度设置」卡片在行星/小行星页签里被一并渲染出来。 -->
          <div
            v-if="visiblePlanets.length === 0"
            style="padding:12px 4px;font-size:12px;color:#909399;"
          >
            没有匹配「{{ searchKeyword }}」的按钮，请更换关键字
          </div>
        </div>
        <div v-else class="phase-settings-card">
          <!-- 容许误差表格 -->
          <div class="tolerance-section">
            <div class="table-container">
              <table class="orb-table">
                <thead>
                  <tr>
                    <th class="corner-cell"></th>
                    <th
                      v-for="(col, index) in toleranceColumns"
                      :key="'col-'+index"
                      class="phase-header-cell"
                      :class="{ 'selected': getPhaseSelected(col, index) }"
                      @click="togglePhaseByHeader(col, index)"
                    >
                      {{ col }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in toleranceRows"
                      :key="'row-'+rowIndex">
                    <td class="row-label" :class="{ 'disabled-row-label': !isPlanetRowSelected(rowIndex) }">{{ row.label }}</td>
                    <td
                      v-for="(col, colIndex) in toleranceColumns"
                      :key="'cell-'+rowIndex+'-'+colIndex"
                      class="orb-cell"
                      :class="{
                        'cell-selected': isPlanetRowSelected(rowIndex) && isCellSelected(rowIndex, colIndex),
                        'cell-disabled': !isPlanetRowSelected(rowIndex),
                        'cell-phase-unselected': isPlanetRowSelected(rowIndex) && !getPhaseSelected(col, colIndex)
                      }"
                      @click="toggleCellSelection(rowIndex, colIndex)"
                    >
                      <div class="dm-inputs" v-if="isPlanetRowSelected(rowIndex) && isCellSelected(rowIndex, colIndex) && getPhaseSelected(col, colIndex) && localMatrix[rowIndex] && localMatrix[rowIndex][colIndex]">
                        <input
                          type="text"
                          v-model="localMatrix[rowIndex][colIndex].degrees"
                          class="orb-input dm-input"
                          placeholder="度"
                          @input="validateInput(localMatrix[rowIndex][colIndex], 'degrees')"
                          @click.stop
                        >
                        <span class="dm-separator">°</span>
                        <input
                          type="text"
                          v-model="localMatrix[rowIndex][colIndex].minutes"
                          class="orb-input dm-input"
                          placeholder="分"
                          @input="validateInput(localMatrix[rowIndex][colIndex], 'minutes')"
                          @click.stop
                        >
                        <span class="dm-separator">′</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <div class="dialog-buttons">
          <button class="btn-cancel" @click="cancel">
            取消
          </button>
          <button class="btn-confirm" @click="confirm">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 行星和虚星的ID集合
// 传统行星: 0-9 (日月水金火木土天海冥), 11(计都), 15(凯龙)
// 特殊行星: 13(月孛)
// 虚拟行星: 1000012(紫炁), 1000013(罗睺), 1000014(MC), 1000015(ASC), 1000016(福点)
const PLANET_IDS = new Set([
  // 传统行星
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 15,
  // 特殊行星：月孛(13)
  13,
  // 虚拟行星：紫炁(1000012), 罗睺(1000013), MC(1000014), ASC(1000015), 福点(1000016)
  1000012, 1000013, 1000014, 1000015, 1000016,
  40, 41, 42, 43, 44, 45, 46, 47,
  // 11181 莉莉丝
  11181
])

const PermissionService = require('@/services/PermissionService')
const phaseToleranceProtocol = require('../../utils/phaseToleranceProtocol')
// 「参与请求」集合（只增不减）：取消只影响画布展示，不影响请求参数与报警判定（2026-10-10）
const canvasParticipation = require('../../features/tianxing/utils/canvasParticipation')

// 按钮名称搜索过滤（只按名称子串匹配，三个配置弹窗可按需复用）
const nameSearchFilter = require('../../utils/nameSearchFilter')

export default {
  name: 'PlanetConfigDialog',
  props: {
    visible: Boolean,
    planets: {
      type: Array,
      default: () => []
    },
    toleranceMatrix: {
      type: Array,
      default: () => []
    },
    phaseToleranceRows: {
      type: Array,
      default: () => []
    },
    defaultMatrix: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      activeTab: 'planets',
      localMatrix: [],
      toleranceColumns: [
        '合相', '二分相', '四分相', '三分相', '六分相',
        '30°', '45°', '135°', '150°', '72°', '144°'
      ],
      toleranceRows: phaseToleranceProtocol.getLegacyPhaseRows(),
      // 相位选择状态（基于列索引，第0列对应ruleIndex 0，以此类推）
      // 列顺序：合相(0), 二分相(10), 四分相(5), 三分相(6), 六分相(3), 30°(1), 45°(2), 135°(7), 150°(9), 72°(4), 144°(8)
      phaseSelections: [true, true, true, true, true, false, false, false, false, false, false],
      // 单元格选择状态（二维数组，行x列）
      cellSelections: [],
      // 「参与请求」集合（2026-10-10，**只增不减**）：
      // 任何"取消"动作都不会把行星从请求里移除，因此被隐藏的行星
      // 数据照常运行、照常参与相位/容许度与报警判定。
      participationIds: [],
      // 按钮名称搜索（2026-10-13）：只过滤「当前标签页的展示列表」，
      // 不影响选中态/参与集合/请求参数，也不会把其它标签页的内容混进来
      searchKeyword: ''
    }
  },
  computed: {
    dialogTitle () {
      if (this.activeTab === 'phases') {
        return '行星相位容许误差设置'
      } else {
        return '选择要显示的天体'
      }
    },
    // 「全选」勾选态 = 「参与请求」态（与按钮同一口径，2026-10-10 还原）
    isAllSelected () {
      const planets = this.filteredPlanets
      return planets.length > 0 && planets.every(p => p.selected)
    },
    isIndeterminate () {
      const planets = this.filteredPlanets
      const selectedCount = planets.filter(p => p.selected).length
      return selectedCount > 0 && selectedCount < planets.length
    },
    filteredPlanets () {
      let list
      if (this.activeTab === 'planets') {
        list = this.planets.filter(p => PLANET_IDS.has(p.id))
      } else {
        const HIDDEN_IDS = new Set([1000017, 1000018, 1000019])
        list = this.planets.filter(p => !PLANET_IDS.has(p.id) && !HIDDEN_IDS.has(p.id))
      }

      // 权限过滤：虚星
      if (!this.hasPermission('asteroids')) {
        const ASTEROID_IDS = new Set([40, 41, 42, 43, 44, 45, 46, 47])
        list = list.filter(p => !ASTEROID_IDS.has(p.id))
      }
      return list
    },
    // ---- 按钮名称搜索（2026-10-13）----
    // 过滤对象是**当前标签页**的 filteredPlanets（行星 / 小行星各自的列表），
    // 因此搜索结果只会来自当前标签页，绝不会混入其它标签页的按钮；
    // 关键字为空即原样返回全部（"没有查询就恢复全部内容"）。
    visiblePlanets () {
      return nameSearchFilter.filterOptions(this.filteredPlanets, this.searchKeyword)
    },
    searchPlaceholder () {
      return this.activeTab === 'asteroids' ? '搜索小行星名称' : '搜索行星名称'
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (val) {
        if (val) {
          this.seedParticipation(this.planets)
          // 打开弹窗清空关键字，避免上次的过滤条件残留（否则会"一打开就少了一半"）
          this.searchKeyword = ''
        }
      }
    },
    // 切换标签页（行星 / 小行星）时清空关键字：
    // 搜索只针对当前标签页，切页后各标签页都从"全部内容"开始，避免误以为漏了按钮
    activeTab () {
      this.searchKeyword = ''
    },
    planets: {
      immediate: true,
      handler (list) {
        // 「参与请求」只增不减（2026-10-10）：把当前已选中的行星并入参与集合，不做删除
        this.seedParticipation(list)
      }
    },
    toleranceMatrix: {
      immediate: true,
      deep: true,
      handler () {
        this.convertFloatToDMSMatrix()
      }
    },
    phaseToleranceRows: {
      immediate: true,
      deep: true,
      handler () {
        this.convertFloatToDMSMatrix()
      }
    }
  },
  created () {
    this.initCellSelections()
  },
  methods: {
    // 全选 / 取消全选（2026-10-10）：
    //   全选     → 全部并入「参与请求」集合（用户主动要全部）；
    //   取消全选 → 清空参与集合，把请求收回（用于清除历史上被"全选"放大的请求参数）。
    toggleSelectAll (val) {
      this.filteredPlanets.forEach(planet => {
        planet.selected = val
        // 与单点按钮同一口径：全选即全部勾选画布展示，全不选即全部取消勾选
        this.$set(planet, 'visibleOnCanvas', val === true)
      })
      this.participationIds = val === true ? this.planets.map(planet => planet.id) : []
    },
    // 行星按钮点击：切换选中状态，并与「画布展示」确认框联动（2026-10-09 调整）。
    //   选中按钮   → 连带勾选确认框（与历史行为一致）；
    //   取消按钮   → 连带取消勾选确认框（本次新增）。
    // 反向约定：勾选确认框会连带选中按钮；取消勾选确认框不影响按钮。
    // 2026-10-10：无论选中还是取消，都只影响「画布展示」；已出现过的行星恒留在请求里。
    togglePlanetSelected (planet) {
      if (!planet) return
      planet.selected = !planet.selected
      this.$set(planet, 'visibleOnCanvas', planet.selected === true)
      if (planet.selected === true) {
        this.markParticipation(planet.id)
      }
    },
    // 画布展示确认框：勾选=画布绘制该分类，取消勾选=停止绘制。
    //   勾选   → 连带把该行星按钮选上（配合"要展示就得先选中"的口径）；
    //   取消勾选 → 只改画布展示状态，不修改 planet.selected，也不触发其它状态变更
    //              （不影响相位/容许度/请求参数）。
    // 2026-10-10：取消勾选**不会**把行星移出请求，数据照常运行、照常参与报警判定。
    toggleCanvasVisible (planet, visible) {
      if (!planet) return
      this.$set(planet, 'visibleOnCanvas', visible === true)
      if (visible === true) {
        planet.selected = true
        // 只有"要展示"才需要并入请求（展示依赖后端返回的数据）；
        // 取消勾选只是停止绘制，既不移出请求、也不新增请求项
        this.markParticipation(planet.id)
      }
    },
    // 记录「参与请求」的对象 id（**只增不减**，永不删除）
    markParticipation (id) {
      this.participationIds = canvasParticipation.markParticipation(this.participationIds, id)
    },
    // 把列表里"当前已选中"的对象并入参与集合（并集，不做删除）
    seedParticipation (list) {
      this.participationIds = canvasParticipation.seedParticipation(this.participationIds, list)
    },
    // 提交给宿主的请求对象列表 = 参与集合 ∪ 当前选中（保证"取消"不会减少请求参数）
    resolveRequestIds () {
      return canvasParticipation.resolveRequestIds(this.participationIds, this.planets)
    },
    // 初始化单元格选择状态（null表示跟随相位状态）
    initCellSelections () {
      const rows = this.toleranceRows.length
      const cols = this.toleranceColumns.length
      // 默认全部为null，跟随相位选择状态
      this.cellSelections = Array(rows).fill(null).map(() => Array(cols).fill(null))
    },
    // 判断行星行是否被选中
    isPlanetRowSelected (rowIndex) {
      const row = this.toleranceRows[rowIndex]
      if (!row) return false

      const planet = this.planets.find(p => {
        if (p.id === row.planetIndex) return true
        if (p.shortName && row.aliases.includes(p.shortName)) return true
        return row.aliases.some(alias => p.name && p.name.includes(alias))
      })
      return planet ? planet.selected : false
    },
    // 判断单元格是否被选中（null表示跟随相位状态）
    isCellSelected (rowIndex, colIndex) {
      if (!this.cellSelections[rowIndex]) {
        // 默认跟随相位状态
        return this.getPhaseSelected(null, colIndex)
      }
      const cellState = this.cellSelections[rowIndex][colIndex]
      if (cellState === null || cellState === undefined) {
        // null/undefined 表示跟随相位状态
        return this.getPhaseSelected(null, colIndex)
      }
      return cellState
    },
    // 切换单元格选中状态
    toggleCellSelection (rowIndex, colIndex) {
      // 行星未选中时不能操作
      if (!this.isPlanetRowSelected(rowIndex)) {
        return
      }
      // 确保行数组存在
      if (!this.cellSelections[rowIndex]) {
        this.$set(this.cellSelections, rowIndex, Array(this.toleranceColumns.length).fill(null))
      }
      // 如果相位列未被选中，先点亮该列（列头勾选），然后只点亮这一格
      if (!this.getPhaseSelected(null, colIndex)) {
        // 将该列所有单元格设置为false（未选中）
        for (let row = 0; row < this.toleranceRows.length; row++) {
          if (!this.cellSelections[row]) {
            this.$set(this.cellSelections, row, Array(this.toleranceColumns.length).fill(null))
          }
          this.$set(this.cellSelections[row], colIndex, false)
        }
        // 只点亮被点击的这一格
        this.$set(this.cellSelections[rowIndex], colIndex, true)
        // 点亮列头
        this.$set(this.phaseSelections, colIndex, true)
        return
      }
      // 相位列已选中时，正常切换单元格状态
      const currentState = this.cellSelections[rowIndex][colIndex]
      // null或true切换为false，false切换为true
      if (currentState === null || currentState === undefined || currentState === true) {
        this.$set(this.cellSelections[rowIndex], colIndex, false)
      } else {
        this.$set(this.cellSelections[rowIndex], colIndex, true)
      }
    },
    // 获取相位是否被选中
    getPhaseSelected (colName, index) {
      return this.phaseSelections[index] || false
    },
    // 通过表头切换相位选择
    togglePhaseByHeader (colName, index) {
      const newPhaseState = !this.phaseSelections[index]
      this.$set(this.phaseSelections, index, newPhaseState)
      // 如果相位变为未选中，重置该列所有单元格状态为null
      if (!newPhaseState) {
        for (let row = 0; row < this.toleranceRows.length; row++) {
          if (this.cellSelections[row]) {
            this.$set(this.cellSelections[row], index, null)
          }
        }
      }
    },
    confirm () {
      // 「参与请求」只增不减（2026-10-10）：提交"参与集合 ∪ 当前选中"，
      // 保证取消按钮 / 取消勾选确认框只影响画布展示，不影响请求参数与报警判定。
      const selectedIds = this.resolveRequestIds()
      this.$emit('save', selectedIds)

      // 画布展示：把「确认框未勾选」的分类 id 告知画布。
      // 画布只据此停止绘制这些分类，选中列表（selectedIds）保持原样，
      // 因此请求参数、相位、容许度等其它方面都不受影响。
      const canvasHiddenIds = this.filteredPlanets
        .filter(p => p.visibleOnCanvas === false)
        .map(p => p.id)
      this.$emit('update-canvas-visible', canvasHiddenIds)

      // 获取选中的相位
      const selectedPhaseIndices = []
      this.phaseSelections.forEach((selected, index) => {
        if (selected) {
          selectedPhaseIndices.push(index)
        }
      })

      const selectedMajorPhases = []
      const selectedMinorPhases = []

      // 根据索引构建主相位和次相位（列索引 -> ruleIndex）
      // 列顺序：合相(0), 二分相(10), 四分相(5), 三分相(6), 六分相(3), 30°(1), 45°(2), 135°(7), 150°(9), 72°(4), 144°(8)
      selectedPhaseIndices.forEach(index => {
        if (index === 0) {
          selectedMajorPhases.push({ name: '合相', ruleIndex: 0, selected: true })
        } else if (index === 1) {
          selectedMajorPhases.push({ name: '二分相', ruleIndex: 10, selected: true })
        } else if (index === 2) {
          selectedMajorPhases.push({ name: '四分相', ruleIndex: 5, selected: true })
        } else if (index === 3) {
          selectedMajorPhases.push({ name: '三分相', ruleIndex: 6, selected: true })
        } else if (index === 4) {
          selectedMajorPhases.push({ name: '六分相', ruleIndex: 3, selected: true })
        } else if (index === 5) {
          selectedMinorPhases.push({ name: '30°', ruleIndex: 1, selected: true })
        } else if (index === 6) {
          selectedMinorPhases.push({ name: '45°', ruleIndex: 2, selected: true })
        } else if (index === 7) {
          selectedMinorPhases.push({ name: '135°', ruleIndex: 7, selected: true })
        } else if (index === 8) {
          selectedMinorPhases.push({ name: '150°', ruleIndex: 9, selected: true })
        } else if (index === 9) {
          selectedMinorPhases.push({ name: '72°', ruleIndex: 4, selected: true })
        } else if (index === 10) {
          selectedMinorPhases.push({ name: '144°', ruleIndex: 8, selected: true })
        }
      })

      if (this.localMatrix.length > 0) {
        const matrixWithFloat = this.localMatrix.map(row => {
          return row.map(cell => {
            const floatValue = this.dmToFloat(cell)
            return floatValue
          })
        })
        this.$emit('update:matrix', matrixWithFloat)
        this.$emit('update:phase-tolerance-rows', phaseToleranceProtocol.convertLegacyMatrixToPhaseToleranceRows(matrixWithFloat))
      }

      // 发射相位选择事件（包含单元格选择状态用于前端过滤）
      this.$emit('update-phases', {
        majorPhases: selectedMajorPhases,
        minorPhases: selectedMinorPhases,
        cellSelections: this.cellSelections,
        toleranceRows: this.toleranceRows,
        toleranceColumns: this.toleranceColumns
      })

      this.$emit('update:visible', false)
    },
    cancel () {
      this.$emit('update:visible', false)
    },
    convertFloatToDMSMatrix () {
      const resolvedState = phaseToleranceProtocol.resolvePhaseToleranceState({
        toleranceMatrix: this.toleranceMatrix && this.toleranceMatrix.length > 0 ? this.toleranceMatrix : this.defaultMatrix,
        phaseToleranceRows: this.phaseToleranceRows
      })

      const convertedMatrix = resolvedState.toleranceMatrix.map(row => {
        return row.map(cell => {
          const floatValue = typeof cell === 'number' ? cell
            : (typeof cell === 'object' ? (cell.value || 0) : 0)

          const degrees = Math.floor(floatValue)
          const remaining = floatValue - degrees
          const minutes = Math.round(remaining * 60)

          return {
            degrees: degrees.toString(),
            minutes: minutes.toString(),
            value: floatValue
          }
        })
      })
      this.localMatrix = convertedMatrix
    },
    dmToFloat (cell) {
      const degrees = parseFloat(cell.degrees) || 0
      const minutes = parseFloat(cell.minutes) || 0
      const value = degrees + (minutes / 60)
      return value
    },
    validateInput (cell, field) {
      let value = cell[field]
      value = value.replace(/[^0-9]/g, '')
      let numValue = parseInt(value) || 0
      if (field === 'minutes') {
        if (numValue >= 60) {
          numValue = 59
          value = '59'
        }
      }
      cell[field] = value
    },
    hasPermission (key) {
      return PermissionService.hasPermission(key)
    }
  }
}
</script>

<style scoped>
/* 样式部分与文档1基本相同，但需要保留原有的tabs样式 */
.dialog-content {
  width: 1060px;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.dialog-tabs {
  padding: 12px 24px 0;
}

.dialog-tabs >>> .el-tabs__nav {
  border: none;
  margin-bottom: -1px;
  display: flex;
  flex-wrap: nowrap;
}

.dialog-tabs >>> .el-tabs__item {
  width: 150px;
  height: 40px;
  padding: 0;
  margin-right: 4px;
  font-size: 0.9rem;
  color: #4a5568;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.dialog-tabs >>> .el-tabs__item:hover {
  color: #3182ce;
  border-color: #bee3f8;
}

.dialog-tabs >>> .el-tabs__item.is-active {
  color: #3182ce;
  font-weight: 600;
  background: white;
  border-bottom: 1px solid white;
  margin-bottom: -1px;
}

.dialog-tabs >>> .el-tabs__active-bar {
  display: none;
}

/* 容许误差区域样式 */
.tolerance-section {
  margin-top: 0px;
}

.dialog-header-inner {
  background: #f0f4f8;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 24px;
}

.small-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #4a5568;
  padding: 4px 0;
}

.dialog-body-content {
  padding: 0px 24px;
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.selection-actions {
  padding: 12px 0 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  align-items: center;
}

.selection-actions >>> .el-checkbox__label {
  font-size: 0.9rem;
  color: #4a5568;
}

.options-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.phase-settings-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.card-header {
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
  margin: 8px 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.orb-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

.orb-table th,
.orb-table td {
  padding: 6px 8px;
  text-align: center;
  font-size: 0.9rem;
  border: 1px solid #e2e8f0;
}

.orb-table th {
  background-color: #edf2f7;
  font-weight: 600;
  min-width: 60px;
}

/* 相位表头单元格样式 */
.phase-header-cell {
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  padding: 10px 8px;
}

.phase-header-cell:hover {
  background-color: #d1e8f5;
}

.phase-header-cell.selected {
  background-color: #3182ce;
  color: white;
  box-shadow: 0 2px 4px rgba(49, 130, 206, 0.3);
}

.phase-header-cell.selected::after {
  content: '✓';
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.85rem;
}

/* 禁用的单元格 */
.disabled-cell {
  background-color: #f7fafc;
  opacity: 0.5;
}

.disabled-cell input {
  background-color: #e2e8f0;
  cursor: not-allowed;
}

/* 未选中行星的行标签样式 */
.disabled-row-label {
  color: #a0aec0;
  background-color: #f7fafc;
}

/* 单元格样式 */
.orb-cell {
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
}

/* 选中的单元格 */
.cell-selected {
  background-color: #e6f7ff;
}

/* 禁用的单元格（行星未选中） */
.cell-disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

/* 相位未选中但行星选中的单元格（可点击） */
.cell-phase-unselected {
  background-color: #f7fafc;
  cursor: pointer;
}

.orb-cell:hover:not(.cell-disabled) {
  background-color: #d1e8f5;
}

.corner-cell {
  background-color: #edf2f7 !important;
  border-right: 1px solid #cbd5e0 !important;
}

.row-label {
  font-weight: 500;
  text-align: center;
  background-color: #f8fafc;
  min-width: 60px;
  font-size: 0.9rem;
}

.dm-inputs {
  display: flex;
  align-items: center;
  gap: 1px;
  justify-content: center;
}

.dm-input {
  width: 22px;
}

.dm-separator {
  font-size: 0.6rem;
  color: #718096;
  font-weight: 500;
  margin: 0 1px;
}

.orb-input {
  padding: 1px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  text-align: center;
  font-size: 0.8rem;
}

.orb-input:focus {
  outline: none;
  border-color: #4299e1 !important;
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2) !important;
}

/* 按钮样式与文档1相同 */
.option-button {
  width: 100%;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  background: #f7fafc;
  color: #4a5568;
  font-size: 0.8rem;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-break: break-word;
  white-space: normal;
}

.option-button:not(.active) {
  background: #f7fafc;
  color: #718096;
  border-color: #cbd5e0;
}

.option-button.active {
  background: #3182ce;
  color: white;
  border-color: #3182ce;
  box-shadow: 0 1px 3px rgba(49, 130, 206, 0.3);
}

.option-button:hover:not(.active) {
  background: #edf2f7;
  border-color: #a0aec0;
}

.option-button.active:hover {
  background: #2c5aa0;
  border-color: #2c5aa0;
}

/* 按钮内文字：占满剩余宽度并居中；右侧固定放「画布展示」确认框 */
.option-label {
  flex: 1;
  text-align: center;
  padding-left: 16px; /* 左侧补偿确认框宽度，保持文字视觉居中 */
}

/* 按钮内的画布展示确认框 */
.option-canvas-check {
  margin-right: 6px;
  flex-shrink: 0;
}

/* 蓝色选中态下确认框用白底蓝勾，保证在蓝底上可见 */
.option-button.active .option-canvas-check >>> .el-checkbox__inner {
  background-color: #ffffff;
  border-color: #ffffff;
}

.option-button.active .option-canvas-check >>> .el-checkbox__inner::after {
  border-color: #3182ce;
}

.dialog-footer {
  padding: 8px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.dialog-buttons {
  display: flex;
  gap: 12px;
}

.btn-confirm, .btn-cancel {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-confirm {
  background: #3182ce;
  color: white;
  border: 1px solid #3182ce;
}

.btn-cancel {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #cbd5e0;
}

@media (max-width: 850px) {
  .options-container {
    grid-template-columns: repeat(4, 1fr);
  }

  .dialog-content {
    width: 95%;
    height: 85vh;
  }
}

@media (max-width: 600px) {
  .options-container {
    grid-template-columns: repeat(3, 1fr);
  }

  .option-button {
    min-height: 28px;
    font-size: 0.75rem;
    padding: 3px 6px;
  }
}
</style>
